import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getProductDisplayName } from '../utils/product';
import { getAllProducts } from '../api/products.api';
import { getCartProductId, getCartStock, reconcileCart, restoreCart } from '../utils/cart';

const CartContext = createContext();
const CART_STORAGE_KEY = 'discoxdiscos_cart';

export const useCart = () => {
  return useContext(CartContext);
};

const getInitialCart = () => {
  try {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    return savedCart ? restoreCart(JSON.parse(savedCart)) : [];
  } catch (error) {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(getInitialCart);
  const cartRef = useRef(cart);
  const catalogRef = useRef(null);
  const requestRef = useRef(null);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [feedback, setFeedback] = useState({
    id: 0,
    open: false,
    message: '',
    severity: 'success',
  });
  const [cartPulseKey, setCartPulseKey] = useState(0);

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart.map(({ product, quantity }) => ({ product, quantity }))));
    } catch (error) {
      // Storage can be blocked or full; the cart still works for this session.
    }
  }, [cart]);

  const commitCart = useCallback(nextCart => {
    cartRef.current = nextCart;
    setCart(nextCart);
  }, []);

  const syncCatalog = useCallback(products => {
    if (!Array.isArray(products)) return;
    catalogRef.current = products;
    const nextCart = reconcileCart(cartRef.current, products);
    commitCart(nextCart);
    setValidationError('');
    return nextCart;
  }, [commitCart]);

  const revalidateCart = useCallback(async ({ refresh = false } = {}) => {
    setValidationError('');
    if (!refresh && catalogRef.current) {
      const nextCart = syncCatalog(catalogRef.current);
      return { ok: nextCart.length > 0 && !nextCart.some(item => item.issue), cart: nextCart };
    }
    setValidating(true);
    try {
      if (!requestRef.current) requestRef.current = getAllProducts();
      const response = await requestRef.current;
      if (!Array.isArray(response.data)) throw new Error('Invalid catalog response');
      // Reconcile the current selection, including edits made while the request was pending.
      const nextCart = syncCatalog(response.data);
      return { ok: nextCart.length > 0 && !nextCart.some(item => item.issue), cart: nextCart };
    } catch (error) {
      setValidationError('No pudimos revisar la selección. Reintentá para continuar.');
      return { ok: false, cart: cartRef.current };
    } finally {
      requestRef.current = null;
      setValidating(false);
    }
  }, [syncCatalog]);

  const showFeedback = (message, severity = 'success') => {
    setFeedback((prev) => ({
      id: prev.id + 1,
      open: true,
      message,
      severity,
    }));
  };

  const closeFeedback = (feedbackId = feedback.id) => {
    setFeedback((prev) => {
      // Ignore a close event from a notification that has already been replaced.
      if (prev.id !== feedbackId) return prev;

      return { ...prev, open: false };
    });
  };

  const triggerCartPulse = () => {
    setCartPulseKey((prev) => prev + 1);
  };

  const addToCart = (product) => {
    const productId = getCartProductId(product);

    if (!productId) {
      showFeedback('No se pudo agregar el disco.', 'error');
      return;
    }

    const productName = getProductDisplayName(product);
    const maxStock = getCartStock(product);

    if (maxStock === null || maxStock <= 0 || product.publicationStatus === 'draft') {
      showFeedback(`"${productName}" está sin stock.`, 'warning');
      return;
    }

    const prevCart = cartRef.current;
    const existingCartItem = prevCart.find(
      (cartItem) => cartItem.product._id === productId
    );

    if (existingCartItem) {
      if (existingCartItem.quantity >= maxStock) {
        showFeedback(
          `Ya agregaste el máximo disponible de "${productName}".`,
          'info'
        );
        triggerCartPulse();
        return;
      }

      showFeedback(`Se agregó otra unidad de "${productName}".`, 'success');
      triggerCartPulse();

      commitCart(prevCart.map((cartItem) =>
        cartItem.product._id === productId
          ? { product: restoreCart([{ product, quantity: 1 }])[0].product, quantity: cartItem.quantity + 1, issue: null }
          : cartItem
      ));
      return;
    }

    showFeedback(`"${productName}" se agregó al carrito.`, 'success');
    triggerCartPulse();

    commitCart([...prevCart, ...restoreCart([{ product, quantity: 1 }])]);
  };

  const removeFromCart = (productId) => {
    commitCart(cartRef.current.filter((cartItem) => cartItem.product._id !== productId));
  };

  const adjustQuantityToStock = productId => {
    if (!catalogRef.current) return;
    const nextCart = reconcileCart(cartRef.current, catalogRef.current).map(item => {
      if (item.product._id !== productId || !['quantity', 'quantityExceeded'].includes(item.issue)) return item;
      const stock = getCartStock(item.product);
      return stock > 0 ? { ...item, quantity: item.issue === 'quantity' ? 1 : stock } : item;
    });
    commitCart(reconcileCart(nextCart, catalogRef.current));
  };

  const clearCart = () => {
    commitCart([]);
    setValidationError('');
    showFeedback('Se vació el carrito.', 'info');
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        clearCart,
        feedback,
        closeFeedback,
        cartPulseKey,
        syncCatalog,
        revalidateCart,
        validating,
        validationError,
        adjustQuantityToStock,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
